import { cn } from '@anytag/shared-utils';
import { useQueryHelper } from '@anytag/shared-hooks';
import { FormAction, Option } from '@anytag/shared-types';
import { AsyncSearchTagsField } from '@anytag/shared-molecules';
import { useUpdateInfluencerTagsByStaffMutation } from '@anytag/graphql';
import { Icon, Button, Popover, PopoverProps, Form } from '@anytag/shared-atoms';

export interface InfluencerTagsPopoverProps extends Omit<PopoverProps, 'width' | 'children' | 'open' | 'onClose'> {
  tags?: readonly string[];
  className?: string;
  influencerId: number;
  refetchQueries?: string[];
  onTagsChange?: (tags: string[]) => void;
  dropdownCss?: string;
}

const formatTagOptions = (tags: readonly string[]) => tags.map<Option<string>>((tag) => ({ label: tag, value: tag }));

interface TagsFormValues {
  tag: string;
  tags: string[];
}

export const InfluencerTagsPopover = ({
  target,
  tags = [],
  dropdownCss,
  onTagsChange,
  influencerId,
  refetchQueries,
  ...restProps
}: InfluencerTagsPopoverProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callUpdateInfluencerTagsByStaff, loading } = useUpdateInfluencerTagsByStaffMutation({ refetchQueries });

  const handleSaveTags =
    (closePopover: () => void): FormAction<TagsFormValues>['onSubmit'] =>
    async ({ tags }) => {
      try {
        await callUpdateInfluencerTagsByStaff({ variables: { input: { tags, influencerId } } });

        onTagsChange?.(tags);
        closePopover();
      } catch (error) {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      }
    };

  return (
    <Popover
      side="bottom"
      align="start"
      target={(targetProps) => (
        // We need empty div here
        <div>
          {(typeof target === 'function' ? target(targetProps) : target) || (
            <div
              onClick={() => targetProps.setOpen()}
              className="bg-gray-light cursor-pointer rounded-sm px-2 py-0.5 text-sm text-active hover:opacity-80"
            >
              <Icon icon="plus" size={7} className="mr-1 mb-0.5" variant="blue" />
              {t('Button.Add Tag')}
            </div>
          )}
        </div>
      )}
      {...restProps}
    >
      {({ closePopover }) => (
        <div className={cn('box-border w-50 p-5 pb-4 shadow-container', dropdownCss)}>
          <Form.Provider<TagsFormValues> values={{ tag: '', tags: [...tags] }} onSubmit={handleSaveTags(closePopover)}>
            {({ watch, setValue }) => {
              const currentTags = watch('tags');

              return (
                <>
                  <AsyncSearchTagsField<keyof TagsFormValues>
                    name="tag"
                    title="Tag List"
                    options={formatTagOptions(tags)}
                    placeholder={t<string>('input tag')}
                    onChange={(field, value) => {
                      setValue(field, ''); // Reset
                      setValue('tags', [...currentTags, value]);
                    }}
                    creatable
                  />

                  <div className="mt-2 flex flex-wrap gap-2">
                    {currentTags.map((tag, index) => (
                      <div
                        key={index}
                        className="bg-gray-light inline-flex h-6 max-w-60 items-center overflow-hidden rounded px-2 align-middle text-ellipsis whitespace-nowrap"
                      >
                        <span className="flex-1 overflow-hidden pr-2 text-sm leading-3 text-ellipsis whitespace-nowrap text-black-90">
                          {tag}
                        </span>
                        <Icon
                          size={8}
                          icon="close"
                          variant="gray"
                          className="ml-1 cursor-pointer hover:text-black-90"
                          onClick={() => {
                            setValue(
                              'tags',
                              currentTags.filter((t) => t !== tag)
                            );
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    type="submit"
                    variant="solid"
                    loading={loading}
                    title={t('Button.Save')}
                    className="mt-4 h-7 w-full"
                  />
                </>
              );
            }}
          </Form.Provider>
        </div>
      )}
    </Popover>
  );
};
